import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookiePreferences {
  accepted: boolean;
  analytics: boolean;
  functionality: boolean;
  targeting: boolean;
  timestamp: string;
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    functionality: true,
    targeting: true,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cj_cookie_consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const saveConsent = (accepted: boolean, customPrefs?: typeof preferences) => {
    const consentData: CookiePreferences = {
      accepted,
      analytics: customPrefs?.analytics ?? false,
      functionality: customPrefs?.functionality ?? false,
      targeting: customPrefs?.targeting ?? false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cj_cookie_consent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent(true, { analytics: true, functionality: true, targeting: true });
  };

  const handleRejectNonEssential = () => {
    saveConsent(false, { analytics: false, functionality: false, targeting: false });
  };

  const handleSavePreferences = () => {
    saveConsent(true, preferences);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <i className="ri-cookie-line text-white text-xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Cookie Preferences</h3>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">
                  We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                  By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                  <Link to="/privacy-policy" className="text-amber-500 hover:text-amber-600 font-medium underline">
                    Privacy Policy
                  </Link>{' '}
                  to learn more about how we handle your data.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                <button
                  onClick={() => setShowPreferences(!showPreferences)}
                  className="px-6 py-3 bg-slate-100 text-slate-900 font-medium rounded-lg hover:bg-slate-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleRejectNonEssential}
                  className="px-6 py-3 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Reject Non-Essential
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Accept All
                </button>
              </div>
            </div>

            {showPreferences && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h4 className="text-lg font-bold text-slate-900 mb-4">Customize Your Cookie Preferences</h4>
                <div className="space-y-4">
                  {/* Strictly Necessary */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="font-semibold text-slate-900">Strictly Necessary</h5>
                        <span className="px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-medium rounded">
                          Always Active
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Essential cookies required for the website to function properly. These cannot be disabled.
                      </p>
                    </div>
                    <div className="relative inline-block w-12 h-6 bg-slate-300 rounded-full opacity-50 cursor-not-allowed">
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform translate-x-6"></div>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <h5 className="font-semibold text-slate-900 mb-1">Analytics & Performance</h5>
                      <p className="text-sm text-slate-600">
                        Help us understand how visitors interact with our website to improve user experience.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-amber-500' : 'bg-slate-300'
                      }`}
                    >
                      <div
                        className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform transition-transform ${
                          preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>

                  {/* Functionality */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <h5 className="font-semibold text-slate-900 mb-1">Functionality</h5>
                      <p className="text-sm text-slate-600">
                        Enable enhanced functionality and personalization, such as remembering your preferences.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, functionality: !preferences.functionality })}
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors cursor-pointer ${
                        preferences.functionality ? 'bg-amber-500' : 'bg-slate-300'
                      }`}
                    >
                      <div
                        className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform transition-transform ${
                          preferences.functionality ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>

                  {/* Targeting */}
                  <div className="flex items-start justify-between gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <h5 className="font-semibold text-slate-900 mb-1">Targeting & Advertising</h5>
                      <p className="text-sm text-slate-600">
                        Used to deliver relevant advertisements and track ad campaign performance.
                      </p>
                    </div>
                    <button
                      onClick={() => setPreferences({ ...preferences, targeting: !preferences.targeting })}
                      className={`relative inline-block w-12 h-6 rounded-full transition-colors cursor-pointer ${
                        preferences.targeting ? 'bg-amber-500' : 'bg-slate-300'
                      }`}
                    >
                      <div
                        className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transform transition-transform ${
                          preferences.targeting ? 'translate-x-6' : 'translate-x-0'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={handleSavePreferences}
                    className="px-8 py-3 bg-amber-500 text-white font-medium rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}